import {
    AfterViewChecked,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbModal } from '@cmps/ng-bootstrap';
import { Store } from 'redux';
import { from, of, Subject, Subscription, timer } from 'rxjs';
import {
    auditTime,
    catchError,
    filter,
    finalize,
    takeUntil,
    tap,
} from 'rxjs/operators';

import {
    DragDropEvent,
    RendererOptions,
    Tab,
    TabEvent,
    TabNode,
} from '../insight/experimental.tabset';
import { PopUpComponent, PopUpService } from '../insight/pop-up.service';
import {
    ConfigureTabReceiver,
    TabContext,
    TabType,
} from '../insight/tabset.model';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import {
    rootInit,
    tabsetAddTab,
    tabsetCloseTab,
    tabsetRenameTab,
    tabsetReorderTab,
    tabsetSetTabStateAction,
    tabsetSwitchTab,
} from './insight-explorer.action';
import { InsightTabConfig } from './insight-explorer.model';
import { RootState, storeStateMapper } from './insight-explorer.state';
import { TabHoverInsightExplorerComponent } from './tab-hover.ie.component';
import { TabRenameInsightExplorerComponent } from './tab-rename.ie.component';
import { TabsetComponent } from './tabset.component';
import { WizardInsightExplorerComponent } from './wizard.ie.component';

@Component({
    selector: '[cmpsInsightExplorerWidget]',
    templateUrl: './widget.ie.component.html',
})
export class WidgetInsightExplorerComponent
    implements
        OnInit,
        AfterViewChecked,
        OnDestroy,
        StateToProps<RootState>,
        ConfigureTabReceiver<InsightTabConfig>
{
    @ViewChild('tabset') private readonly _tabset: TabsetComponent;

    TabType = TabType;

    private readonly _ngOnDestroy: Subject<any>;
    private _storeSub: Subscription;
    state: RootState;
    opts: RendererOptions;
    private readonly _subj: Subject<
        TabEvent | DragDropEvent | { event: MouseEvent; tab: TabNode }
    >;

    tabsetEventsSub: Subscription;
    selectedTab: Tab;
    adjacentTabId: string;
    persistPeriod: boolean;
    private _tabHoverDelaySub: Subscription;
    private _tabHoverRef: PopUpComponent<TabHoverInsightExplorerComponent>;

    get wizardIsOpen(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                WizardInsightExplorerComponent
        );
    }

    get tabRenameIsOpen(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                TabRenameInsightExplorerComponent
        );
    }

    constructor(
        private readonly _el: ElementRef,
        private readonly _ngZone: NgZone,
        private readonly _modalService: NgbModal,
        @Inject(STORE) private readonly _store: Store,
        public popUpService: PopUpService,
    ) {
        this._ngOnDestroy = new Subject();

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const tabMouseleave = () => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._tabHoverDelaySub && this._tabHoverDelaySub.unsubscribe();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._tabHoverRef && this._tabHoverRef.dismiss();
            this._tabHoverRef = null;
        };

        this._subj = new Subject();
        this._subj
            .asObservable()
            .pipe(
                filter((e) => typeof e.event === 'string'),
                tap((e: TabEvent | DragDropEvent) => {
                    if (e.event === 'click' || e.event === 'dblclick') {
                        tabMouseleave();
                    }
                }),
                auditTime(400),
                tap((e: TabEvent | DragDropEvent) => {
                    if (e.event === 'click') {
                        if (e.tab.def.ctx.type === TabType.ADD) {
                            this._startAddTabWizard(
                                e.tab.renderContext.element,
                            );
                        } else {
                            this.popUpService.hide();
                            this.persistPeriod = false;
                            this._store.dispatch(tabsetSwitchTab(e.tab.def.id));
                        }
                    }
                    if (e.event === 'dblclick') {
                        if (e.tab.def.ctx.type !== TabType.ADD) {
                            this._showRenameTab(
                                e.tab,
                                e.tab.renderContext.element,
                            );
                        }
                    }
                }),
                takeUntil(this._ngOnDestroy),
            )
            .subscribe();

        this._subj
            .asObservable()
            .pipe(
                filter((e) => typeof e.event === 'string'),
                tap((e: TabEvent | DragDropEvent) => {
                    if (e.event === 'dragend') {
                        tabMouseleave();
                        this._tabset.node.children.forEach((n) =>
                            n.renderContext.element.classList.remove(
                                'cmps-cdk-bg-color-blue-100',
                            ),
                        );
                    }
                    if (e.event === 'drop') {
                        tabMouseleave();
                        this._store.dispatch(
                            tabsetReorderTab(e.tab.def.id, e.index),
                        );
                    }
                    if (e.event === 'dragenter') {
                        tabMouseleave();
                        this._tabset.node.children.forEach((n) =>
                            n.renderContext.element.classList.remove(
                                'cmps-cdk-bg-color-blue-100',
                            ),
                        );
                        e.tab.renderContext.element.classList.add(
                            'cmps-cdk-bg-color-blue-100',
                        );
                    }
                }),
                takeUntil(this._ngOnDestroy),
            )
            .subscribe();

        this._subj
            .asObservable()
            .pipe(
                filter((e) => e.event instanceof MouseEvent),
                tap((e: { event: MouseEvent; tab: TabNode }) => {
                    if (e.event.type === 'mouseenter') {
                        if (!this.wizardIsOpen && !this.tabRenameIsOpen) {
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            this._tabHoverDelaySub &&
                                this._tabHoverDelaySub.unsubscribe();
                            if (!this._tabHoverRef) {
                                this._tabHoverDelaySub = timer(800)
                                    .pipe(
                                        tap(() => {
                                            this._tabHoverRef =
                                                this.popUpService.show(
                                                    TabHoverInsightExplorerComponent,
                                                    {
                                                        target: () =>
                                                            e.tab.renderContext
                                                                .element,
                                                        placement:
                                                            'bottom-auto',
                                                        containerClass:
                                                            'ie-tab-hover insight-pop-up-container',
                                                        autoClose:
                                                            'click|escape',
                                                    },
                                                );
                                            this._tabHoverRef.componentRef.instance.tab =
                                                e.tab;
                                        }),
                                    )
                                    .subscribe();
                            } else {
                                this._tabHoverRef.props.target = () =>
                                    e.tab.renderContext.element;
                                this._tabHoverRef.componentRef.instance.tab =
                                    e.tab;
                            }
                        }
                    }
                }),
                auditTime(400),
                tap((e: { event: MouseEvent; tab: TabNode }) => {
                    if (e.event.type === 'mouseleave') {
                        tabMouseleave();
                    }
                }),
                takeUntil(this._ngOnDestroy),
            )
            .subscribe();
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<any, RootState>(this._store, (state) =>
            storeStateMapper(state),
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        const prevState = this.state;
                        this.state = state;
                        this.stateToProps(prevState);
                    }),
                ),
            )
            .subscribe();

        this.opts = {
            orientation: 'horizontal',
            tabs: [],
            defaultRenderFn: null,
        };
    }

    ngAfterViewChecked(): void {
        this._bindTabset();
    }

    ngOnDestroy(): void {
        if (this.tabsetEventsSub) {
            this.tabsetEventsSub.unsubscribe();
        }
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
        this._ngOnDestroy.next();
    }

    stateToProps(prevState: RootState): void {
        if (!prevState) {
            this.propsToDispatch();
            return;
        }

        this.opts = { ...this.opts, tabs: this.state.tabs };
        // if (this.tabset) {
        //     this.tabset.node.children.forEach(e => e.renderContext.detectChanges());
        // }

        this.selectedTab = this.state.tabs.find(
            (e) => e.id === this.state.selectedTab,
        );

        this.adjacentTabId = undefined;
        if (this.selectedTab && this.selectedTab.ctx.type !== TabType.ADD) {
            const index = this.state.tabs.indexOf(this.selectedTab);
            if (index !== -1 && index !== 0) {
                this.adjacentTabId = this.state.tabs[index - 1].id;
            }
        }
    }

    propsToDispatch(): void {
        this._store.dispatch(rootInit());
    }

    onSetDefaultTab($event: boolean): void {
        this._store.dispatch(
            tabsetSetTabStateAction(this.selectedTab.id, $event),
        );
    }

    onSetFavouriteTab($event: boolean): void {
        this._store.dispatch(
            tabsetSetTabStateAction(this.selectedTab.id, undefined, $event),
        );
    }

    onAddNewTab($event: Tab<TabContext<InsightTabConfig>>): void {
        this.persistPeriod = true;
        this._store.dispatch(tabsetAddTab($event));
    }

    // TODO emit mouseenter from tabset
    onTabMouseEnter($event: MouseEvent, tab: TabNode): void {
        $event.stopPropagation();
        this._subj.next({ event: $event, tab });
    }

    // TODO emit mouseleave from tabset
    onTabMouseLeave($event: MouseEvent, tab: TabNode): void {
        $event.stopPropagation();
        this._subj.next({ event: $event, tab });
    }

    onTabCloseClick(tab: TabNode): void {
        this.popUpService.hide();
        this.persistPeriod = false;
        this._store.dispatch(tabsetCloseTab(tab.def.id));
    }

    private _bindTabset(): void {
        if (!this._tabset) return;
        if (this.tabsetEventsSub && !this.tabsetEventsSub.closed) return;
        this.tabsetEventsSub = this._tabset.node.events
            .pipe(tap((e) => this._subj.next(e)))
            .subscribe();
    }

    private _startAddTabWizard(target: HTMLElement): void {
        if (this.wizardIsOpen) {
            this.popUpService.hide();
            return;
        }
        const ref = this.popUpService.show(WizardInsightExplorerComponent, {
            target: () => target.querySelector('.ie-tab-add .add'),
            placement: 'bottom-auto',
            triangle: true,
            containerClass: 'ie-wizard insight-pop-up-container',
            backdrop: true,
            backdropClass: 'ie-wizard insight-pop-up-backdrop',
            backdropDismiss: true,
            relativeTo: () =>
                this._el.nativeElement.querySelector('.ie-content > div'),
        });
        ref.componentRef.instance.permissions = this.state.permissions;
        from(ref.result)
            .pipe(
                tap((tab) => {
                    this.persistPeriod = true;
                    this._store.dispatch(tabsetAddTab(tab));
                }),
                catchError(() => of(null)),
                finalize(() => this._tabset.node.detectChanges()),
            )
            .subscribe();
        this._tabset.node.detectChanges();
    }

    private _showRenameTab(tab: TabNode, target: HTMLElement): void {
        if (
            this.tabRenameIsOpen &&
            this.popUpService.active.componentRef.instance.tab === tab
        ) {
            this.popUpService.hide();
            return;
        }
        const ref = this.popUpService.show(TabRenameInsightExplorerComponent, {
            target: () => target,
            placement: 'bottom-auto',
            triangle: true,
            containerClass: 'ie-tab-rename insight-pop-up-container',
            backdrop: true,
            backdropClass: 'ie-tab-rename insight-pop-up-backdrop',
            backdropDismiss: true,
            relativeTo: () =>
                this._el.nativeElement.querySelector('.ie-content > div'),
        });
        ref.componentRef.instance.tab = tab;
        from(ref.result)
            .pipe(
                tap((e: { id: string; label: string }) => {
                    this._store.dispatch(tabsetRenameTab(e.id, e.label));
                }),
                catchError(() => of(null)),
                finalize(() => this._tabset.node.detectChanges()),
            )
            .subscribe();
    }
}
